import { Badge, Button, Group, Loader, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityQuestRO } from '@routellers/types/dist/city-quest/city-quest.ro';
import { CityQuestLocaleStatusEnum } from '@routellers/types/dist/city-quest/enum/city-quest-locale-status.enum';
import { CityQuestStatusEnum } from '@routellers/types/dist/city-quest/enum/city-quest-status.enum';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { api, apiErrorMessage } from '../api';
import { CityQuickSelect, parseCityFilter } from '../components/city-quick-select';
import { EntityThumb } from '../components/entity-thumb';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { QuestCreateDialog } from '../editors/quest-create-dialog';
import { QuestEditor } from '../editors/quest-editor';
import { enumLabel } from '../i18n';
import { contentLanguagesForCountry } from '../utils/content-languages';
import { slicePage } from '../utils/list';

const ALL = 'all';

const statusFromSearch = (value: string | null): string => {
    if (value && Object.values(CityQuestStatusEnum).includes(value as CityQuestStatusEnum)) {
        return value;
    }
    return ALL;
};

const statusColors: Record<CityQuestStatusEnum, string> = {
    [CityQuestStatusEnum.DRAFT]: 'gray',
    [CityQuestStatusEnum.NEED_REVIEW]: 'orange',
    [CityQuestStatusEnum.REJECTED]: 'red',
    [CityQuestStatusEnum.READY]: 'green',
    [CityQuestStatusEnum.PAUSED]: 'blue',
};

const STATUS_FILTER_ORDER = [
    CityQuestStatusEnum.NEED_REVIEW,
    CityQuestStatusEnum.DRAFT,
    CityQuestStatusEnum.REJECTED,
    CityQuestStatusEnum.READY,
    CityQuestStatusEnum.PAUSED,
];

const questTitle = (quest: CityQuestRO): string => quest.title || quest.key;

const readyQuestLanguages = (quest: CityQuestRO): Set<Languages> => {
    const ready = new Set<Languages>();

    for (const locale of quest.locales || []) {
        if (locale.status === CityQuestLocaleStatusEnum.EMPTY) continue;
        if (locale.title?.trim()) ready.add(locale.language);
    }

    return ready;
};

export const QuestsPage = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [rows, setRows] = useState<CityQuestRO[]>([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>(() =>
        statusFromSearch(searchParams.get('status')),
    );
    const [sourceFilter, setSourceFilter] = useState(ALL);
    const [cityFilter, setCityFilter] = useState('all');
    const [editorId, setEditorId] = useState<number | null>(null);
    const [editorTab, setEditorTab] = useState('meta');
    const [creating, setCreating] = useState(false);
    const cityIdFilter = parseCityFilter(cityFilter);

    const load = async () => {
        setLoading(true);

        try {
            const { data } = await api.cityQuestsApi.query(undefined);

            setRows(data || []);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const records = useMemo(() => {
        const q = search.trim().toLowerCase();

        return rows.filter((row) => {
            if (statusFilter !== ALL && row.status !== statusFilter) return false;
            if (sourceFilter === 'partner' && row.createdByPartnerId == null) return false;
            if (sourceFilter === 'admin' && row.createdByPartnerId != null) return false;
            if (cityIdFilter && row.cityId !== cityIdFilter) return false;
            if (!q) return true;

            const title = questTitle(row).toLowerCase();

            return row.key?.toLowerCase().includes(q) || title.includes(q);
        });
    }, [rows, statusFilter, sourceFilter, cityIdFilter, search]);

    useEffect(() => {
        setPage(1);
    }, [statusFilter, sourceFilter, cityFilter, search]);

    const pageRows = slicePage(records, page);

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.quests.title')}
                action={
                    <Button onClick={() => setCreating(true)}>{t('pages.quests.create')}</Button>
                }
            />

            <Group gap='sm' wrap='wrap'>
                <TextInput
                    placeholder={t('pages.quests.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    w={240}
                />
                <CityQuickSelect value={cityFilter} onChange={setCityFilter} />
                <Select
                    w={160}
                    data={[
                        { value: ALL, label: t('pages.quests.statusAll') },
                        ...STATUS_FILTER_ORDER.map((value) => ({
                            value,
                            label: enumLabel(t, 'enums.cityQuestStatus', value),
                        })),
                    ]}
                    value={statusFilter}
                    onChange={(v) => setStatusFilter(v || ALL)}
                />
                <Select
                    w={140}
                    data={[
                        { value: ALL, label: t('pages.quests.sourceAll') },
                        { value: 'partner', label: t('pages.quests.partner') },
                        { value: 'admin', label: t('pages.quests.admin') },
                    ]}
                    value={sourceFilter}
                    onChange={(v) => setSourceFilter(v || ALL)}
                />
            </Group>

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={800}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th style={{ width: 280, maxWidth: 280 }}>
                                        {t('pages.quests.columns.quest')}
                                    </Table.Th>
                                    <Table.Th>{t('pages.quests.columns.city')}</Table.Th>
                                    <Table.Th>{t('pages.quests.columns.locales')}</Table.Th>
                                    <Table.Th>{t('pages.quests.columns.stops')}</Table.Th>
                                    <Table.Th>{t('pages.quests.columns.source')}</Table.Th>
                                    <Table.Th>{t('pages.quests.columns.status')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {pageRows.map((row) => {
                                    const ready = readyQuestLanguages(row);
                                    const expected = contentLanguagesForCountry(row.country);

                                    return (
                                        <Table.Tr
                                            key={row.id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                setEditorTab('meta');
                                                setEditorId(row.id);
                                            }}
                                        >
                                            <Table.Td style={{ width: 280, maxWidth: 280 }}>
                                                <div
                                                    style={{
                                                        maxWidth: 260,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <EntityThumb
                                                        src={row.coverUrl}
                                                        name={questTitle(row)}
                                                        subtitle={row.key}
                                                        radius='md'
                                                    />
                                                </div>
                                            </Table.Td>
                                            <Table.Td>
                                                <Text size='sm'>
                                                    {row.cityTitle ||
                                                        (row.cityId ? `#${row.cityId}` : '—')}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Group gap={4} wrap='wrap'>
                                                    {expected.map((language) => {
                                                        const filled = ready.has(language);

                                                        return (
                                                            <Badge
                                                                key={language}
                                                                size='sm'
                                                                variant={
                                                                    filled ? 'filled' : 'light'
                                                                }
                                                                color={filled ? 'blue' : 'gray'}
                                                            >
                                                                {language.toUpperCase()}
                                                            </Badge>
                                                        );
                                                    })}
                                                    {!expected.length && (
                                                        <Text size='xs' c='dimmed'>
                                                            —
                                                        </Text>
                                                    )}
                                                </Group>
                                            </Table.Td>
                                            <Table.Td>{row.stopsCount ?? 0}</Table.Td>
                                            <Table.Td>
                                                <Badge
                                                    color={
                                                        row.createdByPartnerId != null
                                                            ? 'blue'
                                                            : 'gray'
                                                    }
                                                    variant='light'
                                                >
                                                    {row.createdByPartnerId != null
                                                        ? t('pages.quests.partner')
                                                        : t('pages.quests.admin')}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge
                                                    color={statusColors[row.status] || 'gray'}
                                                    variant='light'
                                                >
                                                    {enumLabel(
                                                        t,
                                                        'enums.cityQuestStatus',
                                                        row.status,
                                                    )}
                                                </Badge>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                                {!pageRows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={7}>
                                            <Text c='dimmed' ta='center' py='md'>
                                                {t('pages.quests.empty')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <ListPagination page={page} totalItems={records.length} onChange={setPage} />
                </>
            )}

            <QuestCreateDialog
                opened={creating}
                onClose={() => setCreating(false)}
                onCreated={(id) => {
                    setCreating(false);
                    setEditorTab('constructor');
                    setEditorId(id);
                    load();
                }}
            />

            <QuestEditor
                opened={editorId != null}
                questId={editorId}
                onClose={() => setEditorId(null)}
                onSaved={load}
                initialTab={editorTab}
            />
        </Stack>
    );
};
